import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/context/LangContext";
import { useProgress } from "@/context/ProgressContext";
import { useAdhanPlayer } from "@/hooks/useAdhanPlayer";
import { KAABA, readLastPos, writeLastPos } from "@/lib/geo";
import { PRAYERS, DEFAULT_METHOD, nextPrayer, readMuezzin, adhanSoundOn, prayerJustPassed } from "@/lib/prayerTimes";
import { readHydration, writeHydration, isActive, dueSlot, recordDrink, snoozeDrink, stopHydration, dayKey } from "@/lib/hydration";
import { PrayerAlertDialog } from "@/components/alerts/PrayerAlertDialog";
import { DrinkAlertDialog } from "@/components/alerts/DrinkAlertDialog";

const AlertsContext = createContext(null);
const FIRE_WINDOW_MS = 90 * 1000;
const PRAYER_FIRED_KEY = "alert_prayer_fired";

// App-wide pop-ups: prayer time (with the favourite muezzin's adhan) and "time to drink" while a
// hydration session runs. Works on every page; one shared adhan player for the whole app.
export function AlertsProvider({ children }) {
  const { tc } = useLang();
  const { progress } = useProgress();
  const adhan = useAdhanPlayer();
  const [pos, setPos] = useState(readLastPos);
  const [prayerAlert, setPrayerAlert] = useState(null); // { key, at }
  const [drinkAlert, setDrinkAlert] = useState(null);   // { slot }
  const prevNextRef = useRef(null);
  const posRef = useRef(pos);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const updatePos = useCallback((p) => { posRef.current = p; setPos(writeLastPos(p)); }, []);

  // Silent refresh of the device position when permission was already granted (no prompt here).
  useEffect(() => {
    if (!navigator.geolocation) return;
    const fetchPos = () => navigator.geolocation.getCurrentPosition(
      (p) => updatePos([p.coords.latitude, p.coords.longitude]), () => {}, { enableHighAccuracy: false, maximumAge: 600000, timeout: 8000 });
    if (navigator.permissions?.query) navigator.permissions.query({ name: "geolocation" }).then((r) => { if (r.state === "granted") fetchPos(); }).catch(() => {});
  }, [updatePos]);

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      // ---- prayer time crossed → pop-up + adhan (once per prayer per day, even across reloads)
      const here = posRef.current || KAABA;
      const next = nextPrayer(here, progressRef.current?.calc_method || DEFAULT_METHOD, new Date(now));
      if (next) {
        const cur = { key: next.key, at: next.at.getTime() };
        const passedKey = prayerJustPassed(prevNextRef.current, cur, now, FIRE_WINDOW_MS);
        prevNextRef.current = cur;
        const stamp = `${dayKey(new Date(now))}:${passedKey}`;
        if (passedKey && localStorage.getItem(PRAYER_FIRED_KEY) !== stamp) {
          localStorage.setItem(PRAYER_FIRED_KEY, stamp);
          if (localStorage.getItem("alert_popup_prayer") !== "off") setPrayerAlert({ key: passedKey, at: now });
          try { navigator.vibrate?.([300, 150, 300]); } catch { /* unsupported */ }
          if (adhanSoundOn()) adhan.play(passedKey, readMuezzin(progressRef.current), true);
        }
      }
      // ---- hydration slot due (or snooze elapsed) → drink pop-up
      const s = readHydration();
      if (s.until && s.until <= now) writeHydration({ ...s, until: null, startedAt: null, by: null, firedSlot: 0, snoozeAt: null });
      else if (isActive(s, now)) {
        const slot = dueSlot(s, now);
        const due = (slot >= 1 && slot !== (s.firedSlot || 0)) || (s.snoozeAt && s.snoozeAt <= now);
        if (due) {
          writeHydration({ ...s, firedSlot: slot, snoozeAt: null });
          setDrinkAlert({ slot: Math.max(slot, 1) });
          try { navigator.vibrate?.([200, 100, 200]); } catch { /* unsupported */ }
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 1 s ticker; reads live values through refs, adhan.play/stop are stable

  const showPrayerAlert = useCallback((key) => setPrayerAlert({ key, at: Date.now(), test: true }), []);
  const showDrinkAlert = useCallback(() => setDrinkAlert({ slot: dueSlot(readHydration()) || 1, test: true }), []);
  const value = useMemo(() => ({ adhan, pos, updatePos, prayerAlert, drinkAlert, showPrayerAlert, showDrinkAlert }), [adhan, pos, updatePos, prayerAlert, drinkAlert, showPrayerAlert, showDrinkAlert]);
  if (process.env.NODE_ENV !== "production") window.__manasikAlerts = { showPrayerAlert, showDrinkAlert };
  const label = prayerAlert ? tc(PRAYERS.find((p) => p.key === prayerAlert.key)?.label) : "";

  return (
    <AlertsContext.Provider value={value}>
      {children}
      <PrayerAlertDialog alert={prayerAlert} label={label} adhan={adhan} muezzinId={readMuezzin(progress)} onClose={() => setPrayerAlert(null)} />
      <DrinkAlertDialog alert={drinkAlert} onDrank={() => { recordDrink(drinkAlert?.slot || 0); setDrinkAlert(null); }}
        onSnooze={() => { snoozeDrink(drinkAlert?.slot || 0); setDrinkAlert(null); }}
        onStop={async () => { await stopHydration({ manual: true }); setDrinkAlert(null); }} onClose={() => setDrinkAlert(null)} />
    </AlertsContext.Provider>
  );
}

export const useAlerts = () => useContext(AlertsContext);
